import { useId, useState, type ReactNode } from 'react'

import { cx } from './cx.ts'

import './disclosure.css'

/**
 * 접힌 줄 하나 — 누르면 아래가 펼쳐진다. 레시피 편집기의 선택 칸 · JSON 절(2026-09-30)에서 걷어 올린 원자다.
 * 소모품 판의 분주 기록(2026-10-01)도 같은 줄이다.
 *
 * 🔴 접혀 있어도 **무엇이 들었는지**가 줄에 보인다 — `meta`(「3곳 적힘」 · 「12건」)가 라벨 옆에 옅게 선다. 펼쳐야 아는 줄을 만들지 않는다.
 * 🔴 접힌 동안 몸통은 DOM 에 없다 — 표 · 입력이 접힌 채로 포커스 · 검색에 걸리지 않는다. `aria-expanded` · `aria-controls` 가 상태를 말한다.
 * 🔴 열림은 **이 컴포넌트의 수명 동안만** 기억한다(로컬 상태). 저장소에 쓰지 않는다 — 다시 열면 `defaultOpen` 이다.
 */
export interface DisclosureProps {
  /** 줄 이름(「선택 칸」 · 「최근 분주 기록」) */
  label: string
  /** 접혀 있어도 보이는 요지(「3곳 적힘」 · 「12건」). 없으면 라벨만 */
  meta?: string | undefined
  /** 줄 오른쪽에 얹는 것(고칠 것 표지 따위). 라벨 · 요지 뒤에 선다 */
  extra?: ReactNode | undefined
  /** 처음 펼쳐진 채로 서나. 기본은 접힘 */
  defaultOpen?: boolean | undefined
  /** 바깥 상자에 더할 클래스(자리마다 여백 · 테를 달리 둘 때) */
  className?: string | undefined
  children: ReactNode
}

export function Disclosure({
  label,
  meta,
  extra,
  defaultOpen = false,
  className,
  children,
}: DisclosureProps) {
  const [open, setOpen] = useState(defaultOpen)
  const bodyId = useId()
  return (
    <div className={cx('a-disc', className)} data-open={open ? 'true' : 'false'}>
      <button
        aria-controls={open ? bodyId : undefined}
        aria-expanded={open}
        className="a-disc__btn"
        onClick={() => setOpen(!open)}
        type="button"
      >
        <span aria-hidden="true" className="a-disc__chev" />
        <span className="a-disc__label">{label}</span>
        {meta === undefined ? null : <span className="a-disc__meta">{meta}</span>}
        {extra}
      </button>
      {open ? (
        <div className="a-disc__body" id={bodyId}>
          {children}
        </div>
      ) : null}
    </div>
  )
}
